import React from "react";

import { motion } from "framer-motion";

import { LOADING_MONOGRAM_PATHS } from "./LoadingLogopaths";

/* ------------------------------------------------------------------
   LOADING LOGO — spinner built from the real LoadingLogo.svg geometry
   (viewBox 0 0 166 166): gradient-ring badge, white inset ring, filled
   gradient disc, monogram. The badge itself gently breathes in place;
   a separate arc rotates around it as the actual "loading" motion.
------------------------------------------------------------------- */

const RED = "#EF0023";
const DEEP_RED = "#930014";

/**
 * `size` in px. `label` is optional text under the mark (e.g. "Loading deals…").
 */
export default function LoadingLogo({ size = 96, label }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
      }}
    >
      <div style={{ position: "relative", width: size, height: size }}>
        {/* Rotating spinner arc — sits just outside the badge's own ring so
            it reads as a loading indicator wrapped around a static logo,
            not as the logo itself moving. */}
        <motion.svg
          width={size}
          height={size}
          viewBox="0 0 166 166"
          style={{ position: "absolute", inset: 0 }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
        >
          <defs>
            <linearGradient id="spinner-grad" x1="83" y1="0" x2="166" y2="166" gradientUnits="userSpaceOnUse">
              <stop stopColor="#FFFFFF" stopOpacity="0" />
              <stop offset="0.55" stopColor="#FFFFFF" stopOpacity="0.85" />
              <stop offset="1" stopColor="#FFFFFF" />
            </linearGradient>
          </defs>
          <circle
            cx="83"
            cy="83"
            r="80"
            fill="none"
            stroke="url(#spinner-grad)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="140 360"
          />
        </motion.svg>

        {/* The badge itself — exact geometry from LoadingLogo.svg — held
            still (just a slow breathing scale) so it reads as the brand
            mark, while the arc above does the spinning. */}
        <motion.svg
          width={size}
          height={size}
          viewBox="0 0 166 166"
          fill="none"
          style={{ position: "absolute", inset: 0 }}
          animate={{ scale: [1, 1.035, 1] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <defs>
            <linearGradient id="badge-fill-grad" x1="83" y1="43.7672" x2="130.928" y2="116.163" gradientUnits="userSpaceOnUse">
              <stop stopColor={RED} />
              <stop offset="1" stopColor={DEEP_RED} />
            </linearGradient>
          </defs>
          <circle cx="83" cy="83" r="55.5" fill="url(#badge-fill-grad)" />
          {LOADING_MONOGRAM_PATHS.map((d, i) => (
            <path key={i} d={d} fill="#FFFFFF" />
          ))}
        </motion.svg>
      </div>

      {label && (
        <motion.span
          animate={{ opacity: [0.55, 1, 0.55] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          style={{
            fontFamily: "system-ui, -apple-system, sans-serif",
            fontSize: 14,
            fontWeight: 500,
            color: "#7A0010",
            letterSpacing: 0.2,
          }}
        >
          {label}
        </motion.span>
      )}
    </div>
  );
}
